import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, LogIn } from "lucide-react";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      const route = user.role === "admin" ? "/admin" : user.role === "brand" ? "/brand" : "/artist";
      navigate(route);
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Hero image */}
      <div 
        className="hidden lg:flex lg:w-1/2 login-hero relative items-center justify-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1767954825607-b4b979fb4a12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxjb25jZXJ0JTIwY3Jvd2QlMjByZWQlMjBsaWdodGluZyUyMGF0bW9zcGhlcmV8ZW58MHx8fHwxNzY4OTczMzI1fDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#B22024]/80 via-[#B22024]/60 to-black/80" />
        <div className="relative z-10 text-center px-12">
          <img 
            src="/logo.png" 
            alt="Sound of Good Times" 
            className="w-72 mx-auto mb-4"
          />
          <p className="text-xl text-white/80 font-body">Campaign Portal</p>
          <div className="mt-8 w-24 h-1 bg-[#F58220] mx-auto"></div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-[#B22024] p-8">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-12">
            <img 
              src="/logo.png" 
              alt="Sound of Good Times" 
              className="w-48 mx-auto mb-2"
            />
            <p className="text-white/70 mt-2">Campaign Portal</p>
          </div>

          <div className="mb-10">
            <h2 className="text-3xl font-heading text-white mb-2">WELCOME BACK</h2>
            <p className="text-white/70">Sign in to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/90 uppercase text-xs tracking-wider font-semibold">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="h-14 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F58220] focus:ring-[#F58220]"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/90 uppercase text-xs tracking-wider font-semibold">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="h-14 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F58220] focus:ring-[#F58220] pr-12"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-[#F58220] hover:bg-[#d97118] text-black font-bold uppercase tracking-wider flex items-center justify-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.3)] transition-all duration-200"
              data-testid="login-submit-button"
            >
              {loading ? (
                <span>Signing in...</span>
              ) : (
                <>
                  <LogIn size={20} />
                  <span>Sign In</span>
                </>
              )}
            </Button>
          </form>

          <div className="mt-12 pt-8 border-t border-white/20 text-center">
            <p className="text-white/50 text-sm">
              Don't have an account? Contact your administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
